import { Component } from '@theme/component';

/**
 * A custom element that manages a card that can open/close additional content.
 *
 * @typedef {object} Refs
 * @property {HTMLDivElement} innerContainer - Inner content that scrolls
 *
 *
 * @extends {Component<Refs>}
 */
class ScrollableMedia extends Component {
  constructor() {
    super();
  }

  connectedCallback() {
    this.refs = {};
    this.querySelectorAll('[ref]').forEach((el) => {
      const name = el.getAttribute('ref');
      if (this.refs[name]) {
        // If already exists, convert to array
        if (!Array.isArray(this.refs[name])) {
          this.refs[name] = [this.refs[name]];
        }
        this.refs[name].push(el);
      } else {
        this.refs[name] = el;
      }
    });

    const observerOptions = {
      root: null, // The viewport
      threshold: 0.5,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && window.location.hash.length == 0) {
          // When the section is in view, activate the scrolljacking
          this.activateScrollJacking();
        }
      });
    }, observerOptions);

    this.observer.observe(this);
  }

  activateScrollJacking() {
    const innerContainer = this.refs.innerContainer;

    let isScrolling = false;
    document.body.style.overflowY = 'hidden';
    document.documentElement.style.overflowY = 'hidden';

    document.body.style.scrollPaddingTop = '0';

    this.scrollIntoView({ behavior: 'smooth' });

    const abortController = new AbortController();

    window.addEventListener(
      'wheel',
      (event) => {
        if (isScrolling) {
          return; // Avoid multiple scrolls at once
        }
        isScrolling = true;

        // Determine the scroll direction (up or down)
        const scrollDirection = event.deltaY > 0 ? 1 : -1;
        const scrollDistance = 50; // Adjust for desired scroll speed

        // Manually scroll the inner container based on the user's input
        innerContainer.scrollBy({
          top: scrollDistance * scrollDirection,
          behavior: 'smooth',
        });

        // Reset the flag after a short delay to allow for the next scroll
        setTimeout(() => {
          isScrolling = false;

          // Check if the inner container has reached the end
          if (
            innerContainer.scrollTop >=
            innerContainer.scrollHeight - innerContainer.clientHeight
          ) {
            // If it has, re-enable page scrolling
            document.body.style.overflowY = '';
            document.documentElement.style.overflowY = '';

            document.body.style.scrollPaddingTop = '';

            this.observer?.disconnect();
            abortController.abort();
          }
        }, 100); // This delay should match the 'smooth' behavior
      },
      { passive: false, signal: abortController.signal },
    ); // Use { passive: false } to enable preventDefault()
  }
}

if (!customElements.get('scrollable-media')) {
  customElements.define('scrollable-media', ScrollableMedia);
}
